import type { InventoryReportItem } from "@/api/report/_models/InventoryReport";
import type { Cin7ProductAvailability } from "@/api/dtos";

/** Where a report SKU landed against Cin7 product availability (report rows only, so no "missing in report"). */
export type InventoryMatchStatus = "matched" | "missingInCin7";

/** One inventory report item checked against Cin7 product availability by SKU + warehouse. */
export interface InventoryReconcileRow {
  sku: string;
  status: InventoryMatchStatus;
  reportItem: InventoryReportItem;
  /** The matching Cin7 row; null when the report SKU/warehouse has no Cin7 match. */
  availability: Cin7ProductAvailability | null;
}

/** The reconciled rows with their matched / missing-in-Cin7 counts. */
export interface InventoryReconcileResult {
  rows: InventoryReconcileRow[];
  matchedCount: number;
  missingInCin7Count: number;
}

/** The inventory report's short warehouse codes → the Cin7 availability Location name. */
const WAREHOUSE_TO_LOCATION: Record<string, string> = {
  FEL: "Estore Logistics Warehouse (Felstead)",
  MJD: "Estore Logistics Warehouse (Sydney)",
};

const normalizeSku = (value: string): string => value.trim().toUpperCase();

const normalizeLocation = (value: string): string => value.trim().toUpperCase();

/** The Cin7 location for a report warehouse code; unknown codes fall back to the raw value. */
const locationForWarehouse = (warehouse: string): string => {
  const code = warehouse.trim().toUpperCase();
  return WAREHOUSE_TO_LOCATION[code] ?? warehouse;
};

const matchKey = (sku: string, location: string): string =>
  `${normalizeSku(sku)}|${normalizeLocation(location)}`;

/**
 * Reconciles inventory report rows against Cin7 product availability by SKU and warehouse.
 * A report row with no Cin7 match is `missingInCin7`; Cin7 rows absent from the report are ignored.
 */
export const reconcileInventory = (
  reportItems: InventoryReportItem[],
  availability: Cin7ProductAvailability[],
): InventoryReconcileResult => {
  const availabilityByKey = new Map<string, Cin7ProductAvailability>();
  for (const row of availability) {
    availabilityByKey.set(matchKey(row.SKU, row.Location), row);
  }

  const rows: InventoryReconcileRow[] = [];

  for (const reportItem of reportItems) {
    const location = locationForWarehouse(reportItem.warehouse);
    const key = matchKey(reportItem.item, location);
    const match = availabilityByKey.get(key) ?? null;
    let status: InventoryMatchStatus = "missingInCin7";
    if (match) {
      status = "matched";
    }
    rows.push({
      sku: reportItem.item,
      status,
      reportItem,
      availability: match,
    });
  }

  const matchedCount = rows.filter((r) => r.status === "matched").length;
  const missingInCin7Count = rows.filter((r) => r.status === "missingInCin7").length;

  return {
    rows,
    matchedCount,
    missingInCin7Count,
  };
};
